import { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { type Dispatch, type UnknownAction } from "redux";
import { withRouter, type RouteComponentProps } from "react-router-dom";
import { useFieldArray, useForm, type Resolver } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { ArrowLeft, ArrowRight } from "lucide-react";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { WFPButton } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";

import {
  listCommodities,
  listExchangeLocations,
  addMarketDemand,
  addMarketDemandReset,
  listTraders,
  listAdministrativeAreas,
  listAggregators,
  listCommodityQualityGrades,
  listCommodityQualityGradesReset,
  listExchangeLocationTypes,
  listUnitsOfMeasure,
  treeAdministrativeAreas,
} from "../../actions";
import { getStatusErrorMessage, valueOrDefault, getCountryObjectFromCache } from "../../utils";
import { adaptCommodities, adaptTrader } from "../../utils/adapters";
import { toDeliveryCostPayload } from "../../utils/delivery-cost";
import { getLocalisedName, orderByLocalName } from "../../utils/i18n";

import { LandUnitOfMeasurementOrUnitOfMeasure } from "../Deposits/DepositCard/deposit-dtos";
import type {
  Trader,
  CustomExchangeLocation,
  ExchangeLocationType,
  AdministrativeAreaOption,
} from "../DirectOfferDetails/OfferForm";
import { DemandDetailsForm, type DepositLine } from "./components/Form";
import { DeliveryStep } from "./components/DeliveryStep";
import type { BrowseMarketParams } from "./components/MatchingDepositsPanel";
import type { AggregatorOption, AdminAreaNode } from "./components/AggregatorSelection";
import {
  createDemandSchema,
  STEP_ONE_FIELDS,
  type DemandFormValues,
  type DemandItemValues,
} from "./schema";

interface CommodityAdapted {
  value: number;
  label: string;
  data: { units?: number[] | null; name_translations?: Record<string, string>; name: string };
}

interface QualityGrade {
  code: string;
  name: string;
}

type UnitOfMeasure = LandUnitOfMeasurementOrUnitOfMeasure & {
  name_translations?: Record<string, string>;
};

export const adaptFromValues = (
  values: DemandFormValues,
  selectedAggregator: AggregatorOption | null
) => {
  const deliveryLocation = values.delivery_location;
  const hasCustomExchangeLocation =
    !!deliveryLocation && typeof deliveryLocation.value === "object";

  return {
    negotiable: values.negotiable,
    tagged_aggregator: selectedAggregator ? selectedAggregator.id : undefined,
    delivery_location: hasCustomExchangeLocation
      ? (deliveryLocation!.value as { deliveryLocationType: string }).deliveryLocationType
      : valueOrDefault(deliveryLocation && deliveryLocation.value, undefined),
    custom_delivery_location: hasCustomExchangeLocation
      ? (deliveryLocation!.value as { custom_delivery_location: number }).custom_delivery_location
      : undefined,
    custom_delivery_location_type: hasCustomExchangeLocation
      ? (deliveryLocation!.value as { custom_delivery_location_type: number })
          .custom_delivery_location_type
      : undefined,
    planned_exchange_time: values.exchange_date ? values.exchange_date.toISOString() : undefined,
    contract_number: valueOrDefault(values.contract_number, undefined),
    comment: valueOrDefault(values.comment, undefined),
    // One delivery cost for the whole demand; the lines carry none of their own.
    delivery_cost: toDeliveryCostPayload(deliveryLocation?.value, values.delivery_cost),
    items: values.items.map(item => ({
      buy_commodity: valueOrDefault(item.buy_commodity, undefined),
      quality: valueOrDefault(item.quality, undefined),
      unit_of_measure: item.unit_of_measure ? Number(item.unit_of_measure) : undefined,
      price: valueOrDefault(item.price, undefined),
      minimum_quantity: valueOrDefault(item.quantity, undefined),
      maximum_quantity: valueOrDefault(item.quantity, undefined),
    })),
  };
};

interface StateProps {
  isFetching: boolean;
  isSubmitting: boolean;
  errorMessage: string;
  commodities: CommodityAdapted[];
  commodityQualityGrades: QualityGrade[];
  unitsOfMeasure: UnitOfMeasure[];
  trader: Trader;
  aggregators: AggregatorOption[];
  customExchangeLocations: CustomExchangeLocation[];
  exchangeLocationTypes: ExchangeLocationType[];
  administrativeAreas: AdministrativeAreaOption[];
  administrativeAreasTree: AdminAreaNode[];
  fieldErrors: unknown;
}

interface DispatchProps {
  listAdministrativeAreas: () => void;
  listAggregators: () => void;
  listCommodities: () => void;
  listExchangeLocations: () => void;
  listExchangeLocationTypes: () => void;
  listUnitsOfMeasure: () => void;
  addMarketDemand: (data: ReturnType<typeof adaptFromValues>) => Promise<unknown>;
  addMarketDemandReset: () => void;
  listTraders: () => void;
  listCommodityQualityGrades: () => void;
  listCommodityQualityGradesReset: () => void;
  treeAdministrativeAreas: (countryId: number) => void;
}

type Props = StateProps & DispatchProps & RouteComponentProps;

type Step = "editing" | "deposits" | "delivery";

const EMPTY_LINE: DemandItemValues = {
  buy_commodity: null,
  quality: "",
  unit_of_measure: "",
  quantity: "",
  price: "",
};

const emptyValues: DemandFormValues = {
  items: [{ ...EMPTY_LINE }],
  negotiable: false,
  exchange_date: null,
  delivery_location: null,
  delivery_cost: "",
  comment: "",
  contract_number: "",
};

export const MarketOfferDetails = ({
  isFetching,
  isSubmitting,
  errorMessage,
  commodities,
  commodityQualityGrades,
  unitsOfMeasure,
  trader,
  aggregators,
  customExchangeLocations,
  exchangeLocationTypes,
  administrativeAreas,
  administrativeAreasTree,
  fieldErrors,
  history,
  listAdministrativeAreas,
  listAggregators,
  listCommodities,
  listExchangeLocations,
  listExchangeLocationTypes,
  listUnitsOfMeasure,
  addMarketDemand,
  addMarketDemandReset,
  listTraders,
  listCommodityQualityGrades,
  listCommodityQualityGradesReset,
  treeAdministrativeAreas,
}: Props) => {
  const { t } = useTranslation();

  const [step, setStep] = useState<Step>("editing");
  const [selectedAggregator, setSelectedAggregator] = useState<AggregatorOption | null>(null);
  const [aggregatorError, setAggregatorError] = useState<string | undefined>(undefined);

  const methods = useForm<DemandFormValues>({
    mode: "onChange",
    defaultValues: emptyValues,
    resolver: zodResolver(createDemandSchema(t)) as unknown as Resolver<DemandFormValues>,
  });
  const { watch, trigger, handleSubmit, control } = methods;
  const { fields, append, remove } = useFieldArray({ control, name: "items" });

  const items = watch("items");
  const currencySymbol = trader.offers_currency?.symbol;

  const commodityOptions = useMemo(
    () => commodities.map(commodity => ({ value: commodity.value, label: commodity.label })),
    [commodities]
  );
  const qualityOptions = useMemo(
    () => commodityQualityGrades.map(grade => ({ value: grade.code, label: grade.name })),
    [commodityQualityGrades]
  );

  // A commodity's selectable units: its `units` ids resolved to {id, code}. One
  // unit → the row shows it as a suffix; several → the row offers a picker.
  const unitOptionsFor = useMemo(() => {
    const byCommodity = new Map(commodities.map(commodity => [commodity.value, commodity]));
    const byId = new Map(unitsOfMeasure.map(unit => [unit.id, unit]));
    return (commodityId: number | null) => {
      const commodity = commodityId ? byCommodity.get(commodityId) : undefined;
      return (commodity?.data.units ?? [])
        .map(unitId => byId.get(unitId))
        .filter((unit): unit is UnitOfMeasure => Boolean(unit))
        .map(unit => ({ id: unit.id, code: unit.code || getLocalisedName(unit) }));
    };
  }, [commodities, unitsOfMeasure]);

  const commodityLabelFor = useMemo(() => {
    const byCommodity = new Map(commodities.map(commodity => [commodity.value, commodity]));
    return (commodityId: number | null) => {
      const commodity = commodityId ? byCommodity.get(commodityId) : undefined;
      return commodity ? getLocalisedName(commodity.data) : undefined;
    };
  }, [commodities]);

  // Computed inline, not memoised: `watch("items")` mutates its array in place
  // as fields change, so the reference is stable and a memo keyed on it would
  // never recompute. Reading the live contents each render keeps this correct.
  const depositLines: DepositLine[] = (items ?? [])
    .filter(item => item.buy_commodity)
    .map(item => ({
      key: `${item.buy_commodity}-${item.quality}-${item.unit_of_measure}`,
      commodityId: item.buy_commodity as number,
      quality: item.quality,
      unitOfMeasureId: item.unit_of_measure
        ? Number(item.unit_of_measure)
        : unitOptionsFor(item.buy_commodity)[0]?.id,
      commodityLabel: commodityLabelFor(item.buy_commodity),
    }));

  useEffect(() => {
    const country = getCountryObjectFromCache();
    listAdministrativeAreas();
    listCommodities();
    listTraders();
    listExchangeLocations();
    listExchangeLocationTypes();
    listUnitsOfMeasure();
    listAggregators();
    listCommodityQualityGrades();
    if (country) {
      treeAdministrativeAreas(country.id);
    }
    return () => {
      addMarketDemandReset();
      listCommodityQualityGradesReset();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Read-only recall of the commodity lines, shown once the user has moved past them.
  const stepOneSummary: { label: string; value: string }[] = [
    {
      label: t("marketOfferDetails.form.commoditiesLabel"),
      value: depositLines
        .map(line => line.commodityLabel)
        .filter(Boolean)
        .join(", "),
    },
    {
      label: t("marketOfferDetails.form.negotiable"),
      value: watch("negotiable") ? t("common.yes") : t("common.no"),
    },
  ];

  const onContinueToDeposits = async () => {
    const valid = await trigger(STEP_ONE_FIELDS);
    if (!valid) {
      return;
    }
    setStep("deposits");
  };

  const onBrowseMarket = (params: BrowseMarketParams) =>
    history.push("/representative/market/", {
      commodity_id: params.commodity_id,
      quality: params.quality,
      unit_of_measure_id: params.unit_of_measure_id,
    });

  const onSelectAggregator = (aggregator: AggregatorOption) => {
    setSelectedAggregator(aggregator);
    setAggregatorError(undefined);
  };

  const onCancel = () => history.push("/representative/offer");

  const onSubmit = handleSubmit(async values => {
    if (!selectedAggregator) {
      setAggregatorError(t("traderDetails.form.mandatoryFieldBlankError"));
      return;
    }
    const data = adaptFromValues(values, selectedAggregator);
    const result = await addMarketDemand(data);
    if (result) {
      history.push("/representative/offer");
    }
  });

  const isInitialLoading = isFetching && commodities.length === 0;

  return (
    <main className="flex flex-col gap-6 py-4 px-6 sm:py-6 sm:px-8 md:py-8 md:px-10">
      <div className="flex flex-col gap-4">
        <h1 className="font-semibold text-2xl">{t("marketOfferDetails.title")}</h1>
      </div>
      {isInitialLoading ? (
        <div className="flex justify-center p-8">
          <Spinner className="size-6" />
        </div>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle>{t("marketOfferDetails.createDemand")}</CardTitle>
            {step === "delivery" && (
              <dl className="mt-3 flex flex-wrap gap-x-8 gap-y-2 text-sm">
                {stepOneSummary.map(({ label, value }) => (
                  <div key={label} className="flex flex-col">
                    <dt className="text-muted-foreground text-xs">{label}</dt>
                    <dd className="font-medium text-foreground">{value}</dd>
                  </div>
                ))}
              </dl>
            )}
          </CardHeader>
          <CardContent className="space-y-6">
            {step === "delivery" ? (
              <DeliveryStep
                methods={methods}
                trader={trader}
                customExchangeLocations={customExchangeLocations}
                administrativeAreas={administrativeAreas}
                exchangeLocationTypes={exchangeLocationTypes}
                aggregators={aggregators}
                administrativeAreasTree={administrativeAreasTree}
                isFetchingAggregators={false}
                selectedAggregator={selectedAggregator}
                onSelectAggregator={onSelectAggregator}
                fieldErrors={fieldErrors}
                aggregatorErrorMessage={aggregatorError}
                currencySymbol={currencySymbol}
                t={t}
              />
            ) : (
              <DemandDetailsForm
                methods={methods}
                fields={fields}
                commodities={commodityOptions}
                qualityOptions={qualityOptions}
                unitOptionsFor={unitOptionsFor}
                currencySymbol={currencySymbol}
                fieldErrors={fieldErrors}
                showDeposits={step === "deposits"}
                depositLines={depositLines}
                onAddCommodity={() => append({ ...EMPTY_LINE })}
                onRemoveCommodity={remove}
                onBrowseMarket={onBrowseMarket}
                t={t}
              />
            )}

            {errorMessage && <p className="text-destructive text-sm">{errorMessage}</p>}

            <div className="flex items-center justify-between border-t border-border pt-4">
              <div>
                {step !== "editing" && (
                  <WFPButton
                    variant="ghost"
                    onClick={() => setStep(step === "delivery" ? "deposits" : "editing")}
                  >
                    <ArrowLeft className="size-4" />
                    {t("common.back")}
                  </WFPButton>
                )}
              </div>
              <div className="flex gap-2">
                <WFPButton
                  variant="secondary"
                  onClick={step === "delivery" ? () => setStep("deposits") : onCancel}
                >
                  {t("common.cancel")}
                </WFPButton>
                {step === "editing" && (
                  <WFPButton onClick={onContinueToDeposits}>
                    {t("common.continue")}
                    <ArrowRight className="size-4" />
                  </WFPButton>
                )}
                {step === "deposits" && (
                  <WFPButton onClick={() => setStep("delivery")}>
                    {t("common.continue")}
                    <ArrowRight className="size-4" />
                  </WFPButton>
                )}
                {step === "delivery" && (
                  <WFPButton onClick={onSubmit} disabled={isSubmitting}>
                    {isSubmitting && <Spinner className="size-4" />}
                    {t("marketOfferDetails.form.sendDemand")}
                    <ArrowRight className="size-4" />
                  </WFPButton>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </main>
  );
};

interface FetchSlice {
  isFetching: boolean;
  errorMessage: string;
}

interface RootState {
  listCommodities: FetchSlice & { data: { results: unknown[] } };
  listCommodityQualityGrades: FetchSlice & { data: { results: QualityGrade[] } };
  listUnitsOfMeasure: FetchSlice & { data: { results: UnitOfMeasure[] } };
  listTraders: FetchSlice & { data: { results: unknown[] } };
  addMarketDemand: { isLoading: boolean; error: unknown };
  listAdministrativeAreas: FetchSlice & { data: { results: { id: number; name: string }[] } };
  listExchangeLocationTypes: FetchSlice & { data: { results: ExchangeLocationType[] } };
  treeAdministrativeAreas: FetchSlice & { data: { administrative_areas: AdminAreaNode[] } };
  listExchangeLocations: { data: { results: CustomExchangeLocation[] } };
  listAggregators: { data: { results: AggregatorOption[] } };
}

export const mapStateToProps = (state: RootState): StateProps => ({
  isFetching:
    state.listCommodities.isFetching ||
    state.listTraders.isFetching ||
    state.listAdministrativeAreas.isFetching ||
    state.listExchangeLocationTypes.isFetching ||
    state.treeAdministrativeAreas.isFetching ||
    state.listCommodityQualityGrades.isFetching,
  isSubmitting: state.addMarketDemand.isLoading,
  errorMessage:
    state.listCommodities.errorMessage ||
    state.listTraders.errorMessage ||
    state.listCommodityQualityGrades.errorMessage ||
    state.treeAdministrativeAreas.errorMessage ||
    getStatusErrorMessage(state.addMarketDemand.error) ||
    "",
  commodities: adaptCommodities(
    orderByLocalName(state.listCommodities.data.results)
  ) as CommodityAdapted[],
  commodityQualityGrades: state.listCommodityQualityGrades.data.results,
  unitsOfMeasure: state.listUnitsOfMeasure.data.results ?? [],
  trader: adaptTrader(state.listTraders.data.results) as Trader,
  aggregators: state.listAggregators.data.results ?? [],
  customExchangeLocations: state.listExchangeLocations.data.results,
  exchangeLocationTypes: state.listExchangeLocationTypes.data.results,
  administrativeAreas: state.listAdministrativeAreas.data.results.map(area => ({
    value: area.id,
    label: area.name,
  })),
  administrativeAreasTree: state.treeAdministrativeAreas.data.administrative_areas,
  fieldErrors: state.addMarketDemand.error,
});

type AppDispatch = Dispatch<UnknownAction>;

const dispatchAction = <TResult,>(dispatch: AppDispatch, action: unknown): TResult =>
  dispatch(action as UnknownAction) as TResult;

export const mapDispatchToProps = (dispatch: AppDispatch): DispatchProps => {
  const countryID = getCountryObjectFromCache().id;

  return {
    listAdministrativeAreas: () =>
      dispatchAction(
        dispatch,
        listAdministrativeAreas({ page_size: 9999, page: 1, country: countryID })
      ),
    listAggregators: () => dispatchAction(dispatch, listAggregators()),
    listCommodities: () =>
      dispatchAction(dispatch, listCommodities({ page_size: 200, grown_by_farmers: true })),
    listExchangeLocations: () => dispatchAction(dispatch, listExchangeLocations()),
    listExchangeLocationTypes: () => dispatchAction(dispatch, listExchangeLocationTypes()),
    listUnitsOfMeasure: () => dispatchAction(dispatch, listUnitsOfMeasure()),
    addMarketDemand: data => dispatchAction<Promise<unknown>>(dispatch, addMarketDemand(data)),
    addMarketDemandReset: () => dispatchAction(dispatch, addMarketDemandReset()),
    listTraders: () => dispatchAction(dispatch, listTraders()),
    listCommodityQualityGrades: () => dispatchAction(dispatch, listCommodityQualityGrades()),
    listCommodityQualityGradesReset: () =>
      dispatchAction(dispatch, listCommodityQualityGradesReset()),
    treeAdministrativeAreas: countryId =>
      dispatchAction(dispatch, treeAdministrativeAreas({}, `${countryId}/administrative_areas/`)),
  };
};

export const ConnectedMarketOfferDetails = connect(
  mapStateToProps,
  mapDispatchToProps
)(withRouter(MarketOfferDetails));
